/* eslint-disable react/no-array-index-key */
import React, { FC, useCallback, useState, useRef, useMemo } from 'react';
import { Button, message, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getTransactions } from '~/api/transactions';
import { Transaction } from '~/types';
import { AmountField, Space, StatusField } from '~/components';
import { Longdash, statusCell } from '~/utils';
import Cell from './Cell';
import { useDeepEffect, usePath, usePermissions } from '~/hooks';
// eslint-disable-next-line import/named
import { Paths } from '~/types/api/generatedApiClient';
import layout from '~/hooks/layout';
import TransactionCard from './TransactionCard';

interface Props {
  className?: string;
  data: Paths.GetTransactionListByProfileId.Responses.$200;
  setData: React.Dispatch<React.SetStateAction<Paths.GetTransactionListByProfileId.Responses.$200>>;
  handleOpen: (data: Transaction) => void;
}

const Transactions: FC<Props> = (props) => {
  const { className, data, setData, handleOpen } = props;
  const { t } = useTranslation('transactions');

  const columns = useMemo<ColumnsType<Transaction>>(
    () => [
      statusCell(),
      {
        title: t('posted'),
        dataIndex: 'postedAmount',
        align: 'right',
        render: (_, record) => {
          return <Cell.ColoredAmount amount={record.postedAmount} />;
        },
      },
      {
        title: t('instructed'),
        dataIndex: 'amount',
        align: 'right',
        render: (_, record) => {
          return <AmountField amount={record.amount} />;
        },
      },
      {
        title: t('description'),
        dataIndex: 'description',
        render: (_, record) => {
          return <Cell.Description record={record} />;
        },
      },
      {
        title: t('dateAndTime'),
        dataIndex: 'createdAt',
        render: (_, record) => {
          return <div>{moment(record.createdAt).format('LL LT')}</div>;
        },
      },
      {
        title: t('runningBalance'),
        dataIndex: 'runningBalance',
        align: 'right',
        render: (_, record) => {
          if (!record?.details?.runningBalances || record.details.runningBalances.length === 0)
            return Longdash;
          const postedBalance = record?.details?.runningBalances?.find(
            (balance) => balance?.code === 'CURRENT_POSTED'
          );
          return <AmountField amount={postedBalance?.amount} />;
        },
      },
      {
        title: t('status'),
        dataIndex: 'status',
        render: (_, record) => {
          return <StatusField data={record.status} addLabel={false} />;
        },
      },
    ],
    [t]
  );

  const {
    params: { filter },
  } = usePath();

  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  const getFilterWithStatuses = (filter: any) => {
    if (!filter?.status) {
      return {
        ...filter,
        status: ['COMPLETED', 'REJECTED', 'PROCESSING', 'AUTHORIZED', 'APPROVED'],
      };
    }
    return filter;
  };

  const loadMore = useCallback(
    async (args: { next?: string; nextPage?: boolean; filterValues?: any }) => {
      const { nextPage = true, next, filterValues } = args || {};
      if (!nextPage) return;
      setLoading(true);
      try {
        const res = await getTransactions({
          limit: 30,
          next,
          ...getFilterWithStatuses(filterValues),
        })();
        if (!res.data?.cursors) return;
        setHasNextPage(!!res.data?.cursors?.next);
        setData((prev) => ({
          cursors: res.data.cursors,
          transactions: (prev.transactions || []).concat(res.data?.transactions || []),
        }));
      } catch (error: any) {
        const err: any = error;
        void message.error(err.message);
      }
      setLoading(false);
    },
    [setData]
  );

  useDeepEffect(() => {
    void (async () => {
      setHasNextPage(true);
      setLoading(true);
      try {
        const res = await getTransactions({ limit: 30, ...getFilterWithStatuses(filter) })();
        setHasNextPage(!!res.data?.cursors?.next);
        setData({
          cursors: res.data.cursors,
          transactions: res.data.transactions,
        });
      } catch (error: any) {
        const err: any = error;
        void message.error(err.message);
      }
      setLoading(false);
    })();
  }, [filter]);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const hasAccessView = usePermissions('business.transactions.view');

  const isMobile = layout.useIsMobile();

  return (
    <div className={className}>
      {isMobile ? (
        <Space direction="vertical" size={16}>
          {data?.transactions?.map((item) => {
            return (
              <TransactionCard
                key={item.id}
                record={item}
                onClick={() => hasAccessView && handleOpen(item)}
              />
            );
          })}
        </Space>
      ) : (
        <Table
          columns={columns}
          dataSource={data?.transactions}
          loading={loading}
          rowKey={(record) => record.id}
          pagination={false}
          rowClassName={getRowClass}
          onRow={(record) => ({
            onClick: () => hasAccessView && handleOpen(record),
          })}
        />
      )}
      <div className="Transactions-LoadMore">
        <Button
          ref={buttonRef}
          loading={loading}
          disabled={!hasNextPage}
          onClick={() => {
            buttonRef.current?.blur();
            void loadMore({
              next: data?.cursors?.next,
              nextPage: hasNextPage,
              filterValues: filter,
            });
          }}
        >
          {hasNextPage ? t('loadMore') : t('allLoaded')}
        </Button>
      </div>
    </div>
  );
};

const getRowClass = (record: Transaction): string => {
  const pointer = 'cursor-pointer';
  if (record.status === 'PENDING') return `Transactions__PENDING status-color__PENDING ${pointer}`;
  return pointer;
};

export default styled(Transactions)`
  .Transactions-LoadMore {
    padding: 16px;
    display: flex;
    justify-content: center;
  }
  .Transactions__PENDING {
    background-color: #fafbfc;
  }
  .Transactions-ExpandedRow {
    font-size: 12px;
    width: 100%;
    .ant-space-item {
      flex-grow: 1;
      .TextField__horizontal {
        width: 100%;
        justify-content: space-between;
      }
    }
  }
`;
